import { CommonModule } from '@angular/common';
import { Component, inject, Input, signal } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { Store } from '@ngrx/store';
import { NgxSpinnerModule } from 'ngx-spinner';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { customPasswordValidator } from '../../../validators/custom-password-validator';
import { confirmPasswordValidator } from '../../../validators/confirm-password-validator';
import { AuthService } from '../../../core/services/auth.service';
import { AppState } from '../../../core/store';
import {
  errorToastAction,
  successToastAction,
} from '../../../core/store/actions/toast.actions';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    SvgIconComponent,
    RouterModule,
    NgxSpinnerModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzCheckboxModule,
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.css',
})
export class ResetPasswordComponent {
  fb = inject(FormBuilder);
  authService = inject(AuthService);
  store = inject(Store<AppState>);
  router = inject(Router);
  route = inject(ActivatedRoute);

  querySub?: Subscription;

  showPassword = signal(false);
  showConfirmPassword = signal(false);

  authForm = this.fb.group({
    password: ['', [Validators.required, customPasswordValidator]],
    confirmPassword: ['', [Validators.required, confirmPasswordValidator]],
    token: ['', [Validators.required]],
  });

  toggleShowPassword(type: 'confirm' | 'main') {
    if (type === 'main') {
      this.showPassword.set(!this.showPassword());
    } else {
      this.showConfirmPassword.set(!this.showConfirmPassword());
    }
  }

  mutation = injectMutation(() => ({
    mutationFn: (data: any) => this.authService.resetPassword(data),
    onSuccess: (data: any) => {
      this.store.dispatch(
        successToastAction({
          autohide: true,
          delay: 7000,
          placement: 'top-end',
          message: 'Password successfully reset',
        }),
      );
      this.authForm.reset();
      this.router.navigate(['/'], {
        queryParams: {
          a: 'login',
        },
      });
    },
    onError: (err) => {
      console.log('err :>> ', err);
      const error = (err as any)?.error?.message;
      this.store.dispatch(
        errorToastAction({
          autohide: true,
          delay: 7000,
          placement: 'top-end',
          message: error || 'Unable to reset password. Try again',
        }),
      );
    },
  }));

  backToLogin() {
    this.router.navigate(['/'], {
      replaceUrl: true,
    });
  }

  onSubmit() {
    if (!this.authForm.valid) {
      this.authForm.markAllAsTouched();
      return;
    }
    this.mutation.mutate(this.authForm.value);
  }

  ngOnInit(): void {
    this.querySub = this.route.queryParamMap.subscribe((params) => {
      const token = params.get('token');

      if (token) this.authForm.patchValue({ token });
    });
  }

  ngOnDestroy(): void {
    this.querySub?.unsubscribe();
  }
}
